import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useContactSelection } from "../../hooks/useContactSelection";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallEndIcon from "@mui/icons-material/CallEnd";
import { getAuthToken } from "../../utils/getAuthToken";
import {
  RecordingSelectBox,
  RecordingRowActions,
  RecordingToolbar,
  RecordingPlayerDialog,
  hasPlayableRecording,
  MAX_SELECTED_RECORDINGS,
} from "./CallRecordings";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 350;

// A call sitting in one of these is still "live" as far as our own DB knows —
// the set force-end (below) is offered for, and the exact set a genuinely
// stuck call (dead on Meta's side, but never got a terminate webhook back)
// gets wedged in forever without it.
const LIVE_STATUSES = ["ringing", "connecting", "connected"];

const STATUS_META = {
  completed: { label: "✅ Answered", color: "#1f7a6c", bg: "#e4f1ee" },
  missed: { label: "📵 Missed", color: "#b8631f", bg: "#f7ead9" },
  abandoned: { label: "🔕 Abandoned", color: "#b8631f", bg: "#f7ead9" },
  declined: { label: "❌ Declined", color: "#a4372a", bg: "#f6e4e0" },
  failed: { label: "⚠️ Failed", color: "#a4372a", bg: "#f6e4e0" },
  ringing: { label: "📲 Ringing", color: "#55605a", bg: "#eceee8" },
  connecting: { label: "📲 Connecting", color: "#55605a", bg: "#eceee8" },
  connected: { label: "📞 In progress", color: "#1f7a6c", bg: "#e4f1ee" },
};

function formatDuration(seconds) {
  const s = seconds || 0;
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatWhen(iso) {
  if (!iso) return "-";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "-";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const callDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const time = date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (callDate.getTime() === today.getTime()) return time;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (callDate.getTime() === yesterday.getTime()) return `Yesterday, ${time}`;
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${time}`;
}

async function fetchCallLogs(params) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}/api/calling/logs`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
}

async function forceEndCall(callId) {
  const token = await getAuthToken();
  const res = await axios.post(
    `${SERVER_DOMAIN}/api/calling/force-end`,
    { callId },
    { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
  );
  return res.data;
}

export default function CallLogsTable() {
  const theme = useTheme();
  const { selectContact } = useContactSelection();
  const user = useSelector((state) => state.auth?.user);
  // This page (unlike Routing/Office Hours/Settings) is open to Calls.View-only
  // users too — same permission gate CallsPage.jsx's canEdit uses, so the End
  // Call button (which the server enforces with roleauth("Calls.Edit")) only
  // renders for whoever could actually use it, instead of showing a control
  // that 403s for most viewers.
  const userPermissions = useSelector((state) => state.userDetails.userPermissions);
  const canForceEnd = userPermissions == null || userPermissions?.permissions?.Calls?.Edit === true;

  const [direction, setDirection] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [endTarget, setEndTarget] = useState(null); // the row pending confirmation, or null
  const [ending, setEnding] = useState(false);
  // Call recording — all of this stays inert (and nothing extra renders) unless an admin has switched
  // recording on for this workspace; the server says so with `recordingEnabled` on every page of results.
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [playerRow, setPlayerRow] = useState(null);
  const searchDebounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (targetPage, append) => {
      const thisRequestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const result = await fetchCallLogs({
          page: targetPage,
          limit: LIMIT,
          direction,
          status,
          search: search || undefined,
        });
        if (thisRequestId !== requestIdRef.current) return; // a newer filter change superseded this request
        setRows((prev) => (append ? [...prev, ...result.data] : result.data));
        setRecordingEnabled(Boolean(result.recordingEnabled));
        setHasMore(result.hasMore);
        setPage(targetPage);
      } catch (err) {
        // best-effort — leave whatever was already loaded on screen
      } finally {
        if (thisRequestId === requestIdRef.current) setLoading(false);
      }
    },
    [direction, status, search]
  );

  // Re-fetch from page 1 whenever a filter changes.
  useEffect(() => {
    setSelectedIds(new Set()); // a selection belongs to the list it was made on
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, status, search]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(value.trim()), SEARCH_DEBOUNCE_MS);
  };

  // selectContact (not a plain navigate) is what actually opens the chat —
  // it dispatches setselectedContactID/fetchMessagesByContact, which is what
  // ChatBody's own effect watches. This route (/home/:accountid/calls) has
  // no :userid segment, so accountid/userid are passed explicitly rather
  // than relying on the hook's own useParams fallback.
  const handleRowClick = (row) => {
    if (!row.contactId || !user) return;
    const accountId = user.accountId || user.AccountID;
    const userId = user._id || user.userId;
    if (!accountId || !userId) return;
    selectContact(row.contactId, { navigate: true, accountid: accountId, userid: userId, clearPrevious: true });
  };

  const toggleRecordingSelected = (callId) => {
    if (selectedIds.has(callId)) {
      const next = new Set(selectedIds);
      next.delete(callId);
      setSelectedIds(next);
      return;
    }
    if (selectedIds.size >= MAX_SELECTED_RECORDINGS) {
      toast.error(`You can select up to ${MAX_SELECTED_RECORDINGS} recordings at a time`);
      return;
    }
    setSelectedIds(new Set(selectedIds).add(callId));
  };

  const selectAllRecordings = (selectable) => {
    if (selectable.length > MAX_SELECTED_RECORDINGS) {
      toast(`Selected the first ${MAX_SELECTED_RECORDINGS} — that's the most one download can hold`);
    }
    setSelectedIds(new Set(selectable.slice(0, MAX_SELECTED_RECORDINGS).map((r) => r.callId)));
  };

  const handleConfirmEnd = async () => {
    if (!endTarget) return;
    setEnding(true);
    try {
      await forceEndCall(endTarget.callId);
      // Patch in place rather than refetching — the filter/search/pagination
      // state stays exactly as the admin left it.
      setRows((prev) =>
        prev.map((r) => (r.callId === endTarget.callId ? { ...r, status: "failed", endedAt: new Date().toISOString() } : r))
      );
      toast.success("Call ended");
      setEndTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to end call");
    } finally {
      setEnding(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        All Calls
      </Typography>

      {/* Filter bar */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 2 }}>
        <Tabs value={direction} onChange={(_, v) => setDirection(v)} sx={{ minHeight: 36 }}>
          <Tab value="all" label="All" sx={{ minHeight: 36 }} />
          <Tab value="inbound" label="Inbound" sx={{ minHeight: 36 }} />
          <Tab value="outbound" label="Outbound" sx={{ minHeight: 36 }} />
        </Tabs>

        <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="completed">Answered</MenuItem>
          <MenuItem value="missed">Missed</MenuItem>
          <MenuItem value="abandoned">Abandoned</MenuItem>
          <MenuItem value="declined">Declined</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </Select>

        <TextField
          size="small"
          placeholder="Search name or number"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 220, ml: "auto" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {recordingEnabled && (
        <RecordingToolbar rows={rows} selectedIds={selectedIds} onSelectAll={selectAllRecordings} onClear={() => setSelectedIds(new Set())} />
      )}

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
        {rows.length === 0 && !loading && (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>No calls match these filters.</Box>
        )}
        {rows.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.failed;
          const isMissedLike = ["missed", "abandoned", "declined", "failed"].includes(row.status);
          return (
            <Box
              key={row.callId}
              onClick={() => handleRowClick(row)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
                cursor: row.contactId ? "pointer" : "default",
                "&:hover": row.contactId ? { bgcolor: "action.hover" } : undefined,
              }}
            >
              {recordingEnabled && (
                <RecordingSelectBox row={row} selected={selectedIds.has(row.callId)} onToggle={toggleRecordingSelected} />
              )}

              {row.direction === "inbound" ? (
                <CallReceivedIcon fontSize="small" sx={{ color: isMissedLike ? "#a4372a" : "text.secondary" }} />
              ) : (
                <CallMadeIcon fontSize="small" sx={{ color: "text.secondary" }} />
              )}

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {row.contactName || row.waNumber}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {row.agentName ? `Agent: ${row.agentName}` : "Agent: —"}
                </Typography>
              </Box>

              <Chip
                label={meta.label}
                size="small"
                sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: 12 }}
              />

              <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 56, textAlign: "right" }}>
                {row.status === "completed" || row.status === "connected" ? formatDuration(row.durationSeconds) : ""}
              </Typography>

              <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 96, textAlign: "right" }}>
                {formatWhen(row.startedAt)}
              </Typography>

              {recordingEnabled && <RecordingRowActions row={row} onPlay={setPlayerRow} />}

              {canForceEnd && LIVE_STATUSES.includes(row.status) && (
                <Tooltip title="End this call">
                  <IconButton
                    size="small"
                    aria-label="End call"
                    onClick={(e) => {
                      // Row itself navigates to the contact's chat on click —
                      // this button lives inside that row, so it must not
                      // also trigger that navigation.
                      e.stopPropagation();
                      setEndTarget(row);
                    }}
                    sx={{
                      color: "#a4372a",
                      "&:hover": { bgcolor: "rgba(164, 55, 42, 0.08)" },
                    }}
                  >
                    <CallEndIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Button size="small" onClick={() => loadPage(page + 1, true)}>
              Load more
            </Button>
          </Box>
        )}
      </Box>

      {recordingEnabled && <RecordingPlayerDialog row={playerRow && hasPlayableRecording(playerRow) ? playerRow : null} onClose={() => setPlayerRow(null)} />}

      <Dialog open={!!endTarget} onClose={() => (!ending ? setEndTarget(null) : null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>End this call?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {endTarget && (
              <>
                This marks the call with <strong>{endTarget.contactName || endTarget.waNumber}</strong> (currently{" "}
                <strong>{STATUS_META[endTarget.status]?.label || endTarget.status}</strong>, started{" "}
                {formatWhen(endTarget.startedAt).toLowerCase()}) as failed/ended in our records.
                <br />
                <br />
                Use this for calls stuck showing in progress that are actually already over — it won't disconnect an
                active call the caller and agent are still genuinely on.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEndTarget(null)} disabled={ending} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmEnd} disabled={ending} variant="contained" color="error" startIcon={ending ? <CircularProgress size={16} color="inherit" /> : <CallEndIcon />}>
            {ending ? "Ending…" : "End call"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
